// The little month in the corner of a planner page.
//
// Every Franklin and Day-Timer page has one, and it does a job the big calendar
// can't: it answers "what day is the 19th" without leaving the page you are
// working on. So it is small, dense, and every day is a link.
//
// Dates are ISO strings throughout and the grid comes from the same monthGrid
// the schedule's month view uses — two implementations of "which Monday does
// this month start on" would drift, and the way you'd find out is a calendar
// that disagrees with the one a tap away.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { monthGrid, monthStart, addMonthsISO } from '../../lib/schedule';
import { fmtDateOpts } from '../../lib/dates';

const HEADS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export default function MiniMonth({ today, selected, marked = [], onPick }) {
  // The month being looked at, which starts as the month today is in but
  // doesn't have to stay there.
  const [anchor, setAnchor] = useState(() => monthStart(today || new Date().toISOString().slice(0, 10)));
  const [followed, setFollowed] = useState(selected);
  if (selected && selected !== followed) {
    setFollowed(selected);
    if (monthStart(selected) !== anchor) setAnchor(monthStart(selected));
  }
  if (!today) return null;

  const { weeks } = monthGrid(anchor);
  const thisMonth = anchor.slice(0, 7);
  const busy = new Set(marked);

  return (
    <section className="planner">
      <header className="planner-band">
        <span>{fmtDateOpts(anchor, { month: 'long', year: 'numeric' })}</span>
        <span className="flex items-center gap-1">
          <Step label="Previous month" onClick={() => setAnchor(addMonthsISO(anchor, -1))} d="M12.5 4.5 7 10l5.5 5.5" />
          {thisMonth !== today.slice(0, 7) && (
            <button
              type="button"
              onClick={() => setAnchor(monthStart(today))}
              className="px-1.5 text-[10px] normal-case tracking-normal text-hunter hover:underline"
            >
              today
            </button>
          )}
          <Step label="Next month" onClick={() => setAnchor(addMonthsISO(anchor, 1))} d="M7.5 4.5 13 10l-5.5 5.5" />
        </span>
      </header>

      <div className="p-2">
        <div className="grid grid-cols-7 gap-px mb-1">
          {HEADS.map((h, i) => (
            <span
              key={i}
              className="text-center text-[10px] font-semibold text-hunter/70"
              // S, M, T, W, T, F, S repeats letters, so a screen reader gets
              // the whole word instead of "T" twice with no way to tell which.
              aria-label={['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][i]}
            >
              {h}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-px">
          {weeks.flat().map((d) => {
            const outside = d.slice(0, 7) !== thisMonth;
            const isToday = d === today;
            // The day you have turned to, when it isn't today: outlined rather
            // than filled, so the filled square always means "now".
            const isShown = !!selected && d === selected && !isToday;
            return (
              <Link
                key={d}
                to={`/schedule?view=day&date=${d}`}
                onClick={(e) => {
                  if (!onPick) return;
                  e.preventDefault();
                  onPick(d);
                }}
                aria-label={fmtDateOpts(d, { weekday: 'long', month: 'long', day: 'numeric' })}
                aria-current={isToday ? 'date' : undefined}
                className={
                  'relative aspect-square flex items-center justify-center rounded text-xs tabular-nums ' +
                  (isToday
                    ? 'bg-hunter text-white font-semibold'
                    : isShown
                      ? 'ring-2 ring-hunter text-hunter font-semibold'
                      : outside
                      // Still rendered, still clickable: the last days of last
                      // month are the ones you look back at, and a blank
                      // there just makes you count squares.
                        ? 'text-ink-300 hover:bg-hunter/5'
                        : 'text-ink-700 hover:bg-hunter/10')
                }
              >
                {Number(d.slice(8, 10))}
                {busy.has(d) && !isToday && (
                  <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-hunter/60" aria-hidden />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Step({ label, onClick, d }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="w-5 h-5 inline-flex items-center justify-center rounded text-hunter/70 hover:text-hunter hover:bg-hunter/10"
    >
      <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d={d} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );
}
